<script>
/**
 * BasePageHeader — cabeçalho base de tela (abaixo da navegação).
 *
 * É um LAYOUT das ações do header (lado direito), composto por:
 *  - slot `filter` — por padrão renderiza o `BaseFilterMenu` (botão "Filtros" +
 *    popover dos campos). O slot permite override total, mas o default É o menu.
 *  - slot `filter-prepend` — controles de filtro extras, renderizados à
 *    ESQUERDA do botão "Filtros" (e, portanto, antes das ações primárias —
 *    ex: o atalho "Modo eu" do Board de Tarefas). Vazio por padrão, então o
 *    layout de quem não o usa não muda.
 *  - slot `actions` — ação(ões) primária(s) à direita (ex: BaseButton "Adicionar").
 *
 * O contrato externo NÃO muda em relação à versão anterior: aceita `:filters`,
 * emite `@apply(flat, meta)` (encaminhado do BaseFilterMenu — inclusive o emit
 * inicial no created e o `meta.live` da busca) e expõe os slots `field-<key>` e
 * `filter-button` (encaminhados ao BaseFilterMenu). As props de UI do menu
 * (`filterButtonLabel`/`menuTitle`/`filterIcon`/`menuWidth`) seguem aqui e são
 * repassadas. `BaseFilterMenu` é registrado globalmente (_base-*).
 *
 * Formato de cada item de `filters`: ver documentação em `_base-filter-menu.vue`.
 *
 * Slots:
 *  - `filter-prepend` — controles extras à esquerda do botão "Filtros".
 *  - `filter` — override do bloco de filtros (default: BaseFilterMenu).
 *  - `filter-button` — activator custom do menu (recebe { on, attrs, count }).
 *  - `field-<key>` — controle custom de campo `type:'slot'` (recebe { field, draft }).
 *  - `actions` — ação(ões) primária(s) à direita.
 */
export default {
  name: 'BasePageHeader',

  props: {
    filters: {
      type: Array,
      default: () => [],
    },
    filterButtonLabel: {
      type: String,
      default: 'Filtros',
    },
    menuTitle: {
      type: String,
      default: 'Filtros da página',
    },
    filterIcon: {
      type: String,
      default: 'filter-new-ux',
    },
    menuWidth: {
      type: [String, Number],
      default: 360,
    },
  },

  computed: {
    hasFilters() {
      return Array.isArray(this.filters) && this.filters.length > 0
    },
  },

  methods: {
    // Encaminha o apply do BaseFilterMenu preservando os DOIS args (flat, meta).
    // (Bind direto perderia o `meta` — daí o método.)
    onFilterApply(flat, meta) {
      this.$emit('apply', flat, meta)
    },
    onFilterClear(payload) {
      this.$emit('clear', payload)
    },
  },
}
</script>

<template>
  <!-- Só as AÇÕES do header (filtros + primário). Título e subtítulo vêm do
       NavBarNavigation (fonte única em crmRoutes); este componente é injetado
       via Teleport no slot page-header-actions, no lado direito do head. -->
  <div class="base-page-header base-page-header__actions d-flex align-center">
    <slot name="filter-prepend" />

    <slot name="filter">
      <BaseFilterMenu
        v-if="hasFilters"
        :filters="filters"
        :filter-button-label="filterButtonLabel"
        :menu-title="menuTitle"
        :filter-icon="filterIcon"
        :menu-width="menuWidth"
        @apply="onFilterApply"
        @clear="onFilterClear"
      >
        <!-- Encaminha os slots de campo/botão (field-<key>, filter-button) que o
             pai passou. `actions`/`filter` não são consumidos pelo menu. -->
        <template
          v-for="(_, slotName) in $scopedSlots"
          v-slot:[slotName]="slotProps"
        >
          <slot :name="slotName" v-bind="slotProps || {}" />
        </template>
      </BaseFilterMenu>
    </slot>

    <slot name="actions" />
  </div>
</template>

<style lang="sass" scoped>
// Só as ações (filtros + primário), alinhadas à direita no head.
.base-page-header
  margin: 0
  border: none

  &__actions
    gap: 8px
</style>
